// The rules of the client application that read more than one answer
// (SEP-293.3).
//
// A question of the bank holds the rule for its own answer: its kind, its
// options and its `showWhen`. Some rules read more than one answer, or they
// compare an answer with today. Those rules live here, under a name.
//
//   - A VISIBILITY RULE opens a question. A question names it with
//     `showWhenRule`, and `isClientApplicationQuestionVisible` runs it.
//   - A FORM RULE gives an error for a question. The validator
//     (`validateClientApplicationAnswers`) runs each one after the checks of
//     each question.
//
// # THE CONTRACT NEVER READS THE CLOCK
//
// A rule reads `context.today`, and the caller gives it: a draft gives the
// date of `clockNow()`, and a snapshot gives the date of submission. A rule
// never calls `new Date()`. When `today` is null, a rule that needs it gives
// its safe answer: a visibility rule keeps its question shown, and a form rule
// gives no error.
//
// Each ticket adds its rules here. SEP-293.5 adds the address history
// (`client-application-address-history.ts`). SEP-293.7 adds the rule that an
// ISA needs a General Investment Account (`client-application-isa-gia.ts`).
// SEP-293.1 adds the rule that opens the detail box of health.

import {
    ADDRESS_HISTORY_NEEDS_SECOND,
    ADDRESS_HISTORY_NEEDS_THIRD,
    CLIENT_APPLICATION_ADDRESS_HISTORY_RULES,
    addressHistoryNeedsSecond,
    addressHistoryNeedsThird,
} from './client-application-address-history';
import { CLIENT_APPLICATION_ISA_NEEDS_GIA_RULE } from './client-application-isa-gia';
import { CLIENT_APPLICATION_PORTAL_SECURITY_RULE } from './client-application-portal-security';
import type {
    ClientApplicationFormRule,
    ClientApplicationRuleId,
    ClientApplicationVisibilityRule,
} from './client-application-questions';

/**
 * The name of the rule that opens the detail box of health (SEP-293.1).
 *
 * Form version 3.0 asks about physical health and about mental health, and
 * one detail box serves both, as on the v6 fact find. A condition list is an
 * AND, so the OR of the two answers is a rule.
 */
export const HEALTH_DETAIL_NEEDED = 'health_detail_needed';

/** The two health answers that open the detail box. */
const HEALTH_DETAIL_ANSWERS: readonly string[] = ['Fair', 'Poor'];

/** True when the physical or the mental health of the client is Fair or Poor. */
export const healthDetailNeeded: ClientApplicationVisibilityRule = (answers) =>
    ['general_health', 'mental_health'].some((id) => {
        const answer = answers[id];
        return typeof answer === 'string' && HEALTH_DETAIL_ANSWERS.includes(answer);
    });

/** The visibility rules of the bank, by the name that a `showWhenRule` gives. */
export const CLIENT_APPLICATION_VISIBILITY_RULES: Readonly<
    Record<ClientApplicationRuleId, ClientApplicationVisibilityRule>
> = {
    [ADDRESS_HISTORY_NEEDS_SECOND]: addressHistoryNeedsSecond,
    [ADDRESS_HISTORY_NEEDS_THIRD]: addressHistoryNeedsThird,
    // SEP-293.1
    [HEALTH_DETAIL_NEEDED]: healthDetailNeeded,
};

/** The form rules of the bank, in the order the validator runs them. */
export const CLIENT_APPLICATION_FORM_RULES: readonly ClientApplicationFormRule[] = [
    ...CLIENT_APPLICATION_ADDRESS_HISTORY_RULES,
    // SEP-293.7
    CLIENT_APPLICATION_ISA_NEEDS_GIA_RULE,
    CLIENT_APPLICATION_PORTAL_SECURITY_RULE,
];
